import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { RdLocalizedNumberPipe } from '../../../pipes/rd-localized-number.pipe';

/**
 * CapitalExpenditures Widget
 */
@Component({
  selector: 'capital-expenditures-widget',
  templateUrl: './capital-expenditures-widget.component.html',
  styleUrls: ['../../common/css/general.scss','./capital-expenditures-widget.component.scss']
})
export class CapitalExpendituresWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Chart column data
   */
  public columnChartData: any[] = [];

  /**
   * Pie chart data
   */
  public pieChartData: any[] = [];

  /**
   * Chart categories
   */
  public categories: any[];

  /**
  * Constructor
  * @param rdLocalizedNumberPipe RdLocalizedNumberPipe
  */
  constructor(injector: Injector, private rdLocalizedNumberPipe: RdLocalizedNumberPipe) {
    super(injector, 'capitalExpenditures',false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("capitalExpenditures", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('capitalExpenditures').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('capitalExpenditures', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    data.rows = [];

    data.rows.push(this.buildRow(data, data.years, 'dcfCapitalExpenditures', 'totalBudgetedCapex'));
    data.rows.push(this.buildRow(data, data.years, 'dcfSinkingFund', 'totalSinkingFund'));
    data.rows.push(this.buildRow(data, data.years, 'dcfRefurbishmentAllowance', 'totalRefurbishmentAllowances'));
    data.rows.push(this.buildRow(data, data.years, 'totalCapitalExpenditure', 'totalCapitalExpenditure', true));
    data.rows.push(this.buildRow(data, data.years, 'dcfCapitalExpenditureAllowance', 'terminalCapitalExpenditureAllowances'));
    data.rows.push(this.buildRow(data, data.years, 'totalCapitalExpenditureInclAllowances', 'totalCapitalExpenditureInclAllowances', true));

    this.reportDesignerTranslator.ready('capitalExpenditures').then(() => {
      this.columnChartData = [
        {
          name: this.reportDesignerTranslator.getString('capitalExpenditures.totalBudgetedCapex'),
          color: '#E30613',
          data: data.years.map((entry) => entry.dcfCapitalExpenditures)
        },
        {
          name: this.reportDesignerTranslator.getString('capitalExpenditures.totalSinkingFund'),
          color: '#636468',
          data: data.years.map((entry) => entry.dcfSinkingFund)
        },
        {
          name: this.reportDesignerTranslator.getString('capitalExpenditures.totalRefurbishmentAllowances'),
          color: '#DBD6C8',
          data: data.years.map((entry) => entry.dcfRefurbishmentAllowance)
        }
      ];

      this.pieChartData = [
        {
          label: this.reportDesignerTranslator.getString('capitalExpenditures.totalBudgetedCapex'),
          color: '#E30613',
          value: Math.abs(data.dcfCapitalExpendituresYearTotal)
        },
        {
          label: this.reportDesignerTranslator.getString('capitalExpenditures.totalSinkingFund'),
          color: '#636468',
          value: Math.abs(data.dcfSinkingFundYearTotal)
        },
        {
          label: this.reportDesignerTranslator.getString('capitalExpenditures.totalRefurbishmentAllowances'),
          color: '#DBD6C8',
          value: Math.abs(data.dcfRefurbishmentAllowanceYearTotal)
        }
      ];

      //calculate percentages to be displayed in chart
      const total = this.pieChartData.map(e => e.value).reduce((prev, curr) => prev + Math.abs(curr), 0);
      for (const entry of this.pieChartData) {
        const value = Math.round(100 * (Math.abs(entry.value) / total) * 100) / 100;
        const formattedValue = this.rdLocalizedNumberPipe.transform(value, 0, false, '', '', 0);
        entry.percentageFormatted = `${formattedValue}%`;
      }
      this.categories = [];
      for (let i = 0; i < data.years.length; ++i) {
        this.categories.push((this.reportDesignerTranslator.getString('capitalExpenditures.year') + (i + 1)));
      }

      this.chartsReady = true;
    });

    return data;
  }

  /**
   * Returns chart label content
  * @param e {any} Event object
   */
  chartLabelContent(e: any): string {
    return e.dataItem.percentageFormatted;
  }

  /**
   * Build new data row
  * @param data {any} Data array
  * @param dataYears {any} Years array
  * @param field {any} Field name
  * @param label {any} Label
  * @param isTotal {boolean} Is row total
   */
  private buildRow(data, dataYears, field, label, isTotal = false) {
    const ret = {
      isTotal: isTotal,
      yearTotal: data[field + 'YearTotal'],
      field: field,
      label: label,
      entries: []
    };

    for (const entry of dataYears) {
      ret.entries.push(entry[field]);
    }

    return ret;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }
}
